import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';

import { ComplexDataTypesEnum } from '~core/models/complex-data-types.enum';
import { KeyValueModel } from '~core/models/key-value.model';
import { FormControlPipe } from '~core/pipes/form-control.pipe';
import { QuickSearchConfigPipe } from '~core/pipes/quick-search-config.pipe';
import { ArcFormControl, ArcFormControlInterface } from '~core/utils/arc-form-control';
import { GlobalConstants } from '~core/utils/global.constants';
import { OptionalType } from '~shared/core/types/optional.type';
import { ButtonToggleGroupComponent } from '../button-toggle-group/button-toggle-group.component';
import { CheckboxComponent } from '../form/checkbox/checkbox.component';
import { DateRangePickerComponent } from '../form/date-range-picker/date-range-picker.component';
import { DatepickerComponent } from '../form/datepicker/datepicker.component';
import { DatetimePickerComponent } from '../form/datetime-picker/datetime-picker.component';
import { GeneralDataSelectComponent } from '../form/general-data-select/general-data-select.component';
import { InputComponent } from '../form/input/input.component';
import { NumberInputComponent } from '../form/number-input/number-input.component';
import { QuickSearchComponent } from '../form/quick-search/quick-search.component';
import { SelectComponent } from '../form/select/select.component';
import { TextareaComponent } from '../form/textarea/textarea.component';
import { MarkdownEditorComponent } from '../markdown-editor/markdown-editor.component';
import { DynamicFormControlTypeEnum } from './models/dynamic-form-control-type-enum';
import { DynamicFormField } from './models/dynamic-form-field';

@Component({
    selector: 'arc-dynamic-form',
    templateUrl: './dynamic-form.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        ReactiveFormsModule,
        InputComponent,
        TextareaComponent,
        NumberInputComponent,
        CheckboxComponent,
        DatepickerComponent,
        DatetimePickerComponent,
        DateRangePickerComponent,
        ButtonToggleGroupComponent,
        SelectComponent,
        GeneralDataSelectComponent,
        MarkdownEditorComponent,
        QuickSearchComponent,
        TranslocoPipe,
        FormControlPipe,
        QuickSearchConfigPipe
    ]
})
export class DynamicFormComponent implements OnInit, OnChanges {
    @Input() formFields: DynamicFormField[] = [];
    @Input() classLayout = 'flex flex-col';
    @Output() readonly formSubmitted = new EventEmitter<any>();

    readonly DynamicFormControlTypeEnum = DynamicFormControlTypeEnum;
    readonly ComplexDataTypesEnum = ComplexDataTypesEnum;
    readonly GlobalConstants = GlobalConstants;

    formGroup!: FormGroup;
    items: { key: string; field: DynamicFormField; control: AbstractControl }[] = [];

    private readonly formBuilder = inject(FormBuilder);
    private dateRangeFormFieldKey = 'DateRange';

    asFormGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    ngOnInit(): void {
        this.formGroup = this.setupFormFields();
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['formFields'] && !simpleChanges['formFields'].isFirstChange()) {
            this.formGroup = this.setupFormFields();
        }
    }

    getSortedOptions(options: KeyValueModel<string, string>[]): KeyValueModel<string, string>[] {
        const sorted = options.sort((a, b) => {
            if (!a.value || !b.value) {
                return 0;
            }
            if (a.value?.toLowerCase() < b.value?.toLowerCase()) {
                return -1;
            }
            if (a.value?.toLowerCase() > b.value?.toLowerCase()) {
                return 1;
            }
            return 0;
        });
        return sorted;
    }

    async submitForm(): Promise<void> {
        this.formGroup.markAllAsTouched();
        this.formGroup.updateValueAndValidity();

        if (this.formGroup.valid) {
            const data: Record<string, any> = {};

            for (const formField of this.formFields) {
                const key = formField.key;
                data[key] = await this.getControlValue(formField);
            }

            this.formSubmitted.emit(data);
        }
    }

    async getFormValues(): Promise<Record<string, any>> {
        const values: Record<string, any> = {};

        for (const formField of this.formFields) {
            const key = formField.key;
            values[key] = await this.getControlValue(formField);
        }

        return values;
    }

    private setupFormFields(): FormGroup {
        const formGroupConfig: any = {};

        if (this.formFields.length > 0) {
            let fromDateControl: OptionalType<ArcFormControlInterface>;
            let fromDateDynamicFormField: DynamicFormField;

            for (let formField of this.formFields) {
                formField = new DynamicFormField(formField);
                const validators =
                    !formField.isNullable && formField.type !== DynamicFormControlTypeEnum.Label ? Validators.required : undefined;
                const formControl = new ArcFormControl(formField.value, validators);

                // API is not sending any DynamicFormControlTypeEnum.DateRange
                if (this.isPartOfDateRange(formField)) {
                    if (formField.key === 'FromDate') {
                        // save the fromDate to be used when the toDate is found.
                        fromDateControl = formControl;
                        fromDateDynamicFormField = formField;
                    } else {
                        const toDateControl = formControl;
                        const formFieldKey = this.dateRangeFormFieldKey;
                        const formGroup = new FormGroup({ FromDate: fromDateControl!, ToDate: toDateControl });
                        formGroupConfig[formFieldKey] = formGroup;
                        formField.key = formFieldKey;
                        formField.label = `${fromDateDynamicFormField!.label} - ${formField.label}`;
                        formField.type = DynamicFormControlTypeEnum.DateRange;

                        this.items.push({ key: formFieldKey, field: formField, control: formGroup });
                    }
                } else {
                    const formFieldKey = formField.key;
                    formGroupConfig[formFieldKey] = formControl;

                    if (formField.type === DynamicFormControlTypeEnum.Select && formField.isNullable) {
                        formField.selectOptions = !!formField.selectOptions
                            ? [{ value: undefined, label: '-' }, ...formField.selectOptions]
                            : [];
                    }

                    this.items.push({ key: formFieldKey, field: formField, control: formControl });
                }
            }
        }

        return this.formBuilder.group(formGroupConfig);
    }

    private async getControlValue(formField: DynamicFormField): Promise<OptionalType<any>> {
        let value = this.isPartOfDateRange(formField)
            ? (this.formGroup.controls[this.dateRangeFormFieldKey] as FormGroup).controls[formField.key].value
            : this.formGroup.controls[formField.key].value;

        if (formField.type === DynamicFormControlTypeEnum.Checkbox) {
            value = value === 'true' || value === true;
        } else if (formField.type === DynamicFormControlTypeEnum.Date || formField.type === DynamicFormControlTypeEnum.DateTime) {
            value = !value ? value : this.dateToISOString(value);
        } else if (formField.type === DynamicFormControlTypeEnum.Number || formField.type === DynamicFormControlTypeEnum.Decimal) {
            // eslint-disable-next-line no-null/no-null
            value = value === undefined || value === null ? value : Number(value);
        }

        return Promise.resolve(value);
    }

    private dateToISOString(value: any): string {
        const date = new Date(value);
        return date.toISOString();
    }

    private isPartOfDateRange(formField: DynamicFormField): boolean {
        return (
            (formField.type === DynamicFormControlTypeEnum.Date || formField.type === DynamicFormControlTypeEnum.DateRange)
            && (((formField.key === 'FromDate' && this.formFields.find(f => f.key === 'ToDate'))
                || (formField.key === 'ToDate' && this.formFields.find(f => f.key === 'FromDate'))) as boolean)
        );
    }
}
